import { createReducer } from '@reduxjs/toolkit';
import { Reducer } from 'redux';

import { countryDefaultCode } from '@/i18n/countries';
import { FilterItem, PServiceFilterItem, pServiceFilterKeys } from '@/types/filters';
import { _window } from '@/utils/ssr-window';

import {
    filterDeselect,
    filterSelect,
    filtersLoad,
    filtersLoadSuccess,
    filtersReset,
    PServiceFiltersActions,
    pServiceFiltersKey,
    PServiceFiltersState,
} from './polako-service-filters.actions';
import { Filters, FiltersStored } from './polako-service-filters.types';

const filtersState = (): Filters => ({
    city: [],
    // tag: [],
    language: [],
    countryCode: countryDefaultCode,
});

const initialState: PServiceFiltersState = {
    filtersAvailable: filtersState(),
    filtersSelected: filtersState(),
};

export const polakoServiceFiltersReducer: Reducer<PServiceFiltersState, PServiceFiltersActions> = createReducer(
    initialState,
    (builder) => {
        builder
            .addCase(filtersLoad, (state: PServiceFiltersState) =>
                Object.assign<object, PServiceFiltersState, Partial<PServiceFiltersState>>({}, state, initialState),
            )
            .addCase(filtersLoadSuccess, (state: PServiceFiltersState, action: ReturnType<typeof filtersLoadSuccess>) =>
                Object.assign<object, PServiceFiltersState, Partial<PServiceFiltersState>>(
                    {},
                    state,
                    restoreStored(action.payload.filters),
                ),
            )
            .addCase(filterSelect, (state: PServiceFiltersState, action: ReturnType<typeof filterSelect>) => {
                const statePart = setSelected(state.filtersAvailable, action.payload, true);
                return Object.assign<object, PServiceFiltersState, Partial<PServiceFiltersState>>({}, state, {
                    filtersAvailable: statePart?.filtersAvailable || state.filtersAvailable,
                    filtersSelected: statePart?.filtersSelected || state.filtersSelected,
                });
            })
            .addCase(filterDeselect, (state: PServiceFiltersState, action: ReturnType<typeof filterDeselect>) => {
                const statePart = setSelected(state.filtersAvailable, action.payload, false);
                return Object.assign<object, PServiceFiltersState, Partial<PServiceFiltersState>>({}, state, {
                    filtersAvailable: statePart?.filtersAvailable || state.filtersAvailable,
                    filtersSelected: statePart?.filtersSelected || state.filtersSelected,
                });
            })
            .addCase(filtersReset, (state: PServiceFiltersState) => {
                const available = JSON.parse(
                    JSON.stringify(state.filtersAvailable),
                ) as PServiceFiltersState['filtersAvailable'];
                for (const fKey of pServiceFilterKeys) {
                    for (const item of available[fKey]) item.isSelected = false;
                }
                const selected = filtersState();
                selected.countryCode = state.filtersSelected.countryCode;
                return Object.assign<object, PServiceFiltersState, Partial<PServiceFiltersState>>({}, state, {
                    filtersAvailable: available,
                    filtersSelected: selected,
                });
            });
    },
);

function setSelected(
    _filtersAvailable: PServiceFiltersState['filtersAvailable'],
    filter: PServiceFilterItem,
    isSelected: boolean,
): Pick<PServiceFiltersState, 'filtersAvailable' | 'filtersSelected'> | null {
    const filtersAvailable = JSON.parse(JSON.stringify(_filtersAvailable)) as PServiceFiltersState['filtersAvailable'];
    const itemIx = filtersAvailable[filter.key].findIndex((item: FilterItem) => item.value === filter.value);
    if (itemIx < 0) return null;
    filtersAvailable[filter.key][itemIx].isSelected = isSelected;
    const filtersSelected = filtersState();
    for (const fKey of pServiceFilterKeys) {
        filtersSelected[fKey] = filtersAvailable[fKey].filter((item) => item.isSelected);
    }
    filtersSelected.countryCode = _filtersAvailable.countryCode;
    return { filtersAvailable: filtersAvailable, filtersSelected: filtersSelected };
}

function restoreStored(
    filtersAvailable: PServiceFiltersState['filtersAvailable'],
): Pick<PServiceFiltersState, 'filtersAvailable' | 'filtersSelected'> {
    const filtersSelected = filtersState();
    const stored = JSON.parse(
        _window?.localStorage?.getItem(`${pServiceFiltersKey}_${filtersAvailable.countryCode}`) || '{}',
    ) as Partial<FiltersStored>;
    if (Object.keys(stored).length > 0) {
        for (const fKey of pServiceFilterKeys) {
            for (const value of stored[fKey] || []) {
                const itemIx = filtersAvailable[fKey].findIndex((item) => item.value === value);
                if (itemIx < 0) continue;
                filtersAvailable[fKey][itemIx].isSelected = true;
                filtersSelected[fKey].push(filtersAvailable[fKey][itemIx]);
            }
        }
    }
    filtersSelected.countryCode = filtersAvailable.countryCode;
    return { filtersAvailable: filtersAvailable, filtersSelected: filtersSelected };
}
